import { useLocalStorage } from '@festina-life/ui/hooks'
import { useCallback, useDeferredValue, useMemo } from 'react'

import { useNavigationParams } from '../../system/useNavigationParams'
import { useOperationsContext } from '../../workspace/operationsContext'
import {
  filterExistentTabLinks,
  isExistentTabLink,
} from './filterExistentTabLinks'
import { mergeIntoStoredTabLinks } from './mergeIntoStoredTabLinks'
import type { TabLink } from './types'
import { useCloseTab } from './useCloseTab'
import { useCurrentPathAsTabLink } from './useCurrentPathAsTabLink'
import { useEnsureCurrentRouteInTabLinks } from './useEnsureCurrentRouteInTabLinks'
import { useEnsureNavigationToActivePath } from './useEnsureNavigationToActivePath'
import { useEnsureTabLimits } from './useEnsureTabLimits'

export const useTabNavigation = () => {
  const { server, branch } = useNavigationParams()
  const tabsStorageKey = `tabs@${server}-${branch ?? 'none'}`
  const [storedTabLinks, setStoredTabLinks] =
    useLocalStorage<TabLink[]>(tabsStorageKey)
  const deferredStoredTabLinks = useDeferredValue(storedTabLinks)

  const {
    templateOperations,
    parameterDefinitionCreateOperations,
    parameterValueEntityOperations,
    configuredEnums,
    registeredTenantCreateOperations,
    glMapOperations,
    reconciliationSpecCreateOperations,
    allAnnulOperationsByKey,
    isLoading,
  } = useOperationsContext()
  const currentPathAsTabLink = useCurrentPathAsTabLink()

  const operations = useMemo(
    () => ({
      templateOperations,
      parameterDefinitionCreateOperations,
      parameterValueEntityOperations,
      configuredEnums,
      registeredTenantCreateOperations,
      glMapOperations,
      reconciliationSpecCreateOperations,
      allAnnulOperationsByKey,
    }),
    [
      templateOperations,
      parameterDefinitionCreateOperations,
      parameterValueEntityOperations,
      configuredEnums,
      registeredTenantCreateOperations,
      glMapOperations,
      reconciliationSpecCreateOperations,
      allAnnulOperationsByKey,
    ],
  )

  const visibleTabLinks = useMemo(() => {
    if (!deferredStoredTabLinks || isLoading) {
      return deferredStoredTabLinks
    }
    return filterExistentTabLinks({
      tabLinks: deferredStoredTabLinks,
      operations,
      currentPathAsTabLink,
    })
  }, [deferredStoredTabLinks, isLoading, operations, currentPathAsTabLink])

  /**
   * A route pointing at an item that does not exist must not become a tab: it would
   * be filtered out again on every render, so the effect adding it would re-add it
   * indefinitely.
   */
  const trackableCurrentPathAsTabLink = useMemo(
    () =>
      !isLoading &&
      currentPathAsTabLink &&
      isExistentTabLink(currentPathAsTabLink, operations)
        ? currentPathAsTabLink
        : undefined,
    [currentPathAsTabLink, isLoading, operations],
  )

  /**
   * Hooks acting on the visible tab links must not write them back as they are:
   * that would drop the tabs hidden by `filterExistentTabLinks` from storage.
   */
  const updateVisibleTabLinks = useCallback(
    (nextVisibleTabLinks: TabLink[]) =>
      setStoredTabLinks(
        mergeIntoStoredTabLinks({
          storedTabLinks,
          visibleTabLinks,
          nextVisibleTabLinks,
        }),
      ),
    [storedTabLinks, visibleTabLinks, setStoredTabLinks],
  )

  useEnsureNavigationToActivePath({
    visibleTabLinks,
  })

  useEnsureCurrentRouteInTabLinks({
    storedTabLinks,
    tabsStorageKey,
    updateTabLinks: setStoredTabLinks,
    currentPathAsTabLink: trackableCurrentPathAsTabLink,
  })

  useEnsureTabLimits({
    // Until operations are loaded the tab links are not filtered yet, so hidden
    // tabs would count towards the limit and could be evicted.
    visibleTabLinks: isLoading ? undefined : visibleTabLinks,
    updateTabLinks: updateVisibleTabLinks,
  })

  const { closeTab, closeInactive, closeAll } = useCloseTab({
    visibleTabLinks,
    updateTabLinks: updateVisibleTabLinks,
  })

  return {
    close: closeTab,
    tabLinks: visibleTabLinks,
    closeAll,
    closeInactive,
  }
}
